import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { onConfirm } from 'Shared/helpers'
import { isExtraColumn, isRegistrationExtraColumn, buildRegistrationPopupData } from '../column_helpers'
import GolfHubPageStatus from 'Shared/constants/golfHubPageStatus'
import RegistrationStatus from 'Shared/constants/registrationStatus'
import PopupTypes from '../popup_types'
import { FaIcon } from '../../shared/components'
import {
  GlgTableRow,
  GlgTableCell,
  GlgDropdown,
  GlgTooltipIcon,
  GlgLabel,
} from 'SharedComponents'
import Toggle from 'react-bootstrap-toggle'
import RegistrationCardComponent from './registration_card_component'

const getGolfHubPageValue = (golfHubPage, preventFromPublish) => {
  if (golfHubPage === GolfHubPageStatus.PUBLISHED && !preventFromPublish){
    return 'golfhubpublished'
  } else if (golfHubPage === GolfHubPageStatus.CREATED && !preventFromPublish){
    return 'golfhubcreated'
  } else {
    return 'golfhubabsent'
  }
}

const translate = (key) => (window.I18n ? window.I18n.t(key) : '')

const buildRegistrationOption = ({ openRegistrationData, id, openPopup, timeZone }) => {
  if (openRegistrationData.disabledRegistrationButton) {
    return {
      label: 'Open Registration',
      disabled: true,
      tooltip: 'You cannot open registration because you did not save the Open Registration form. Please verify and save the form to enable this option.',
      id,
    }
  }

  const popup = () => openPopup(PopupTypes.INVITATION_LIST_REGISTRATION, buildRegistrationPopupData(id, openRegistrationData, timeZone))

  if (openRegistrationData.canInvitationsList || openRegistrationData.canUseMembershipPrograms) {
    return { label: 'Open/Close Registration', onClick: popup }
  }
  if (openRegistrationData.openRegistrationStatus === RegistrationStatus.OPEN) {
    return {
      label: 'Close Registration',
      onClick: popup,
      tooltip: 'Your registration will end automatically on the set end date. If you close registration by clicking here, the system will reset the selected end date.',
      id,
    }
  }
  // registration is closed
  return {
    label: 'Open Registration',
    onClick: popup,
    tooltip: 'Your registration will start automatically on the set start date. If you open registration by clicking here, the system will reset the selected start date.',
    id,
  }
}

const Handle = ({canManage, ...props}) => {
  const style = canManage ? 'clickable' : 'hidden'
  return (
    <span className={style} { ...props }>
      <FaIcon icon="bars" />
    </span>
  ) 
}

const LeagueRowComponent = ({
  id, name, product, startDate, registrationMessage,
  seasonId, seasonName, tourId, tourName,
  golfers, leagueURL, portalURL, canManage,
  categoryId, categoryStatus, isSortable,
  includeCategoryColumn, categoryName,
  openRegistrationData, timeZoneDisplay,
  userIsCustomerManager, userIsTourManager, userIsVisageAdmin,
  showTours, toursEnabled, showSeasonsColumn, seasonsEnabled, showRegistration, golfHubRegistration,
  deleteLeague, archiveLeague, restoreLeague, openPopup, toggleGolfHubPublished, toggleGolfhubRegistration,
  isTeamCaptain, isShotManager, golfHub, golfHubEnabled, golfHubPage, links, isTmLite, hasPremiumRegistration, isTeamMatch, userIsGroupLeader,
  ...props
}) => {
  let dropdownOptions
  const userIsManager = userIsCustomerManager || userIsTourManager
  const isGroupProduct = product && product.startsWith('group_')

  const golfHubRegistrationActionable = golfHubEnabled && golfHubRegistration &&
    [ GolfHubPageStatus.CREATED, GolfHubPageStatus.PUBLISHED ].includes(String(golfHub))
  if (canManage) {
    dropdownOptions = []
    if (!isTmLite) {
      dropdownOptions.push({
        label: 'Go to Portal',
        url: portalURL,
      })
    }
    dropdownOptions.push({
      label: `Go to ${ _.capitalize(window.I18n.t(`${product}_name`)) }`,
      url: leagueURL,
    })
    if (userIsManager || (userIsGroupLeader && isGroupProduct)) {
      if (golfHubEnabled && golfHubPage.enabled && links.golfhubDirectoryPage && !userIsGroupLeader) {
        dropdownOptions.push({
          label: translate('views.golfhub.customer_center_events_actions.go_to_gh_page_button'),
          onClick: () => window.open(links.golfhubDirectoryPage, '_blank', 'noopener,noreferrer'),
        })
      }
      if (hasPremiumRegistration && showRegistration && openRegistrationData.hasOpenRegistration) {
        dropdownOptions.push({
          label: translate('views.golfhub.customer_center_events_actions.edit_registration'),
          url: links.registrationSetupURL,
        })
      }
      if (golfHubRegistrationActionable) {
        dropdownOptions.push({
          label: translate('views.golfhub.customer_center_events_actions.edit_registration'),
          url: links.golfhubRegistrationSetupURL,
        })
      }
      if (!isTmLite){
        dropdownOptions.push({
          label: `Clone ${ _.capitalize(window.I18n.t(`${product}_name`)) }`,
          onClick: () => openPopup(PopupTypes.CLONE_EVENT, {
            leagueId: id,
          }),
        })
        if (!userIsGroupLeader) {
          dropdownOptions.push({
            label: 'Change Category',
            onClick: () => openPopup(PopupTypes.CHANGE_CATEGORY, {
              categoryId,
              leagueName: name,
              leagueId: id,
            }),
          })
        }
        if (seasonsEnabled && !userIsGroupLeader) {
          dropdownOptions.push({
            label: 'Change Season',
            onClick: () => openPopup(PopupTypes.CHANGE_SEASON, {
              leagueName: name,
              leagueId: id,
              seasonId,
            }),
          })
        }
      }
      if (toursEnabled && !userIsGroupLeader) {
        dropdownOptions.push({
          label: 'Change Tour',
          onClick: () => openPopup(PopupTypes.CHANGE_TOUR, {
            leagueName: name,
            leagueId: id,
            tourId,
          }),
        })
      }
      if (golfHubRegistrationActionable) {
        dropdownOptions.push({
          label: !golfHubPage.golfHubRegistrationOpen ?
            translate('views.golfhub.customer_center_events_actions.gh_action_open_registration') :
            translate('views.golfhub.customer_center_events_actions.gh_action_close_registration'),
          onClick: () => toggleGolfhubRegistration(id, golfHubPage.golfHubRegistrationOpen, links.toggleRegistration),
        })
      }
      if (hasPremiumRegistration && showRegistration && openRegistrationData.hasOpenRegistration) {
        dropdownOptions.push(buildRegistrationOption({ openRegistrationData, id, openPopup, timeZone: props.timeZone }))
      }

      let archiveOption
      if (categoryStatus === 'archived') {
        archiveOption = {
          label: 'Restore',
          onClick: () => restoreLeague(id),
        }
      } else {
        archiveOption = {
          label: 'Archive',
          onClick: () => {
            onConfirm(`Are you sure you want to archive ${ name }? ` +
              'You will still be able to access this event and you can always restore it.', () => {
              archiveLeague(id)
            })
          },
        }
      }
      dropdownOptions.push(archiveOption)
      if (isTeamMatch) {
        dropdownOptions.push({
          label: 'Delete',
          disabled: true,
          tooltip: 'This event is linked to a Team Match Season. To remove it, delete the Team Match Season instead.',
        })
      } else {
        dropdownOptions.push({
          label: 'Delete',
          onClick: () => {
            onConfirm(`Are you sure you want to delete ${ name }? `, () => {
              deleteLeague(id)
            })
          },
        })
      }
    }
  }

  // When preventEventPublish is true, the page cannot be published/unpublished until the blocking condition is resolved (missing date, invalid address, etc.)
  const createEventAction = golfHub !== GolfHubPageStatus.PUBLISHED && golfHub !== GolfHubPageStatus.CREATED || golfHubPage.preventEventPublish

  return (
    <GlgTableRow { ...props } isSortable={ isSortable }>
      {isSortable && (
        <GlgTableCell>
          <Handle { ...props.dragHandleProps } canManage={canManage} />
        </GlgTableCell>
      )}
      <GlgTableCell>
        <a href={ canManage || isTeamCaptain || isShotManager ? leagueURL : portalURL }>
          { name }
        </a>
      </GlgTableCell>
      {golfHubEnabled && !userIsGroupLeader && (
        userIsManager ? (
          <div className={`${ createEventAction ? 'golfhub-action-button' : 'golfhub-toggle-buttons'}`}>
            <GlgTableCell>
              <GlgLabel type={ getGolfHubPageValue(golfHub, golfHubPage.preventEventPublish)}>
                { createEventAction ?
                    <a className='golf_hub_setup_link' href={ links.setupGolfhubData }> 
                      { translate('views.golfhub.customer_center_events_actions.create') }
                    </a>
                  :
                    <div className='golfhub-published-container'>
                      <div className='page-status-content'>
                        <Toggle onClick={ () => toggleGolfHubPublished(id, golfHubPage.enabled, links.togglePublished) }
                          on={ translate('views.golfhub.customer_center_events_actions.publish') }
                          off={ translate('views.golfhub.customer_center_events_actions.unpublished') }
                          className='rounded published-toggle'
                          id={`toggle-golfhub-page-published-${id}`}
                          active={ golfHubPage.enabled }
                        />
                      </div>
                    </div>
                }
              </GlgLabel>
            </GlgTableCell>
          </div>
        ) : (
          <div className="non-manager">
            <GlgTableCell>
              <GlgLabel type={ getGolfHubPageValue(golfHub)}>
                { golfHub }
              </GlgLabel>
            </GlgTableCell>
          </div>
        )
      )}
      {userIsManager && includeCategoryColumn && (
        <GlgTableCell>
          { categoryName }
        </GlgTableCell>
      )}
      {userIsManager && showSeasonsColumn && (
        <GlgTableCell>
          <GlgLabel type="success">
            { seasonName }
          </GlgLabel>
        </GlgTableCell>
      )}
      {userIsManager && showTours && (
        <GlgTableCell>
          <GlgLabel type="success">
            { tourName }
          </GlgLabel>
        </GlgTableCell>
      )}
      <GlgTableCell>
        { startDate }
      </GlgTableCell>
      <GlgTableCell>
        { golfers }
      </GlgTableCell>
      {((!userIsManager && showRegistration) || (userIsManager && (hasPremiumRegistration || golfHubEnabled))) &&
        <RegistrationCardComponent
          id={ id }
          openRegistrationData={ openRegistrationData }
          registrationMessage={ registrationMessage }
          timeZoneDisplay={ timeZoneDisplay }
          timeZone={ props.timeZone }
          openPopup={ openPopup }
          hasPremiumRegistration={ openRegistrationData.hasPremiumRegistration }
          golfHubEnabled={ golfHubEnabled }
          golfHubRegistration={ golfHubRegistration }
          golfhubStatus={ golfHub }
          golfHubRegistrationOpen={ golfHubPage.golfHubRegistrationOpen }
          toggleGolfhubRegistration={ toggleGolfhubRegistration }
          links={ links }
          userIsManager= { userIsManager }
        />
      }
      {isExtraColumn(props.sortingColumn) && (
        <GlgTableCell>
          { props[props.sortingColumn] }
          {props[props.sortingColumn] && isRegistrationExtraColumn(props.sortingColumn) && (
            <GlgTooltipIcon tooltip={ `Date and time are displayed in ${ timeZoneDisplay } time` } />
          )}
        </GlgTableCell>
      )}
      <GlgTableCell>
        {userIsVisageAdmin || isTeamCaptain || isShotManager ? (
          <a href={ leagueURL }>
            Go to { _.capitalize(product) }
          </a>
        ) : (canManage ? (
          <GlgDropdown items={ dropdownOptions } direction="left" />
        ) : (
          <a href={ portalURL }>
            Go to Portal
          </a>
        ))}
      </GlgTableCell>
    </GlgTableRow>
  )
}

LeagueRowComponent.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  golfHub: PropTypes.string,
  product: PropTypes.string.isRequired,
  startDate: PropTypes.string,
  registrationMessage: PropTypes.string,
  seasonId: PropTypes.string,
  seasonName: PropTypes.string,
  tourId: PropTypes.string,
  tourName: PropTypes.string,
  golfers: PropTypes.array,
  leagueURL: PropTypes.string.isRequired,
  portalURL: PropTypes.string,
  categoryId: PropTypes.string,
  categoryName: PropTypes.string,
  categoryStatus: PropTypes.string,
  canManage: PropTypes.bool.isRequired,
  includeCategoryColumn: PropTypes.bool,
  openRegistrationData: PropTypes.object.isRequired,
  timeZoneDisplay: PropTypes.string.isRequired,
  userIsCustomerManager: PropTypes.bool.isRequired,
  userIsTourManager: PropTypes.bool.isRequired,
  userIsVisageAdmin: PropTypes.bool.isRequired,
  isSortable: PropTypes.bool.isRequired,
  // From the sorting library; need to apply it to the handle
  dragHandleProps: PropTypes.object,
  showTours: PropTypes.bool.isRequired,
  toursEnabled: PropTypes.bool.isRequired,
  showSeasonsColumn: PropTypes.bool.isRequired,
  seasonsEnabled: PropTypes.bool.isRequired,
  showRegistration: PropTypes.bool.isRequired,
  sortingColumn: PropTypes.string,
  deleteLeague: PropTypes.func.isRequired,
  openPopup: PropTypes.func.isRequired,
  archiveLeague: PropTypes.func.isRequired,
  restoreLeague: PropTypes.func.isRequired,
  toggleGolfHubPublished: PropTypes.func.isRequired,
  toggleGolfhubRegistration: PropTypes.func,
  isTeamCaptain: PropTypes.bool.isRequired,
  timeZone: PropTypes.string.isRequired,
  isShotManager: PropTypes.bool.isRequired,
  golfHubEnabled: PropTypes.bool,
  golfHubRegistration: PropTypes.bool,
  golfHubPage: PropTypes.shape({
    enabled: PropTypes.bool,
    preventEventPublish: PropTypes.bool,
    golfHubRegistrationOpen: PropTypes.bool,
  }),
  links: PropTypes.shape({
    togglePublished: PropTypes.string,
    setupGolfhubData: PropTypes.string,
    golfhubDirectoryPage: PropTypes.string,
    registrationSetupURL: PropTypes.string,
    golfhubRegistrationSetupURL: PropTypes.string,
    toggleRegistration: PropTypes.string,
  }),
  isTmLite: PropTypes.bool,
  userIsGroupLeader: PropTypes.bool,
  hasPremiumRegistration: PropTypes.bool,
  isTeamMatch: PropTypes.bool,
}

LeagueRowComponent.defaultProps = {
}

Handle.propTypes = {
  canManage: PropTypes.bool,
}

export default LeagueRowComponent
